import { FC, useEffect, useState } from "react";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";
import { Box, Typography, CircularProgress } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
    createCashDrawerReasonAction,
    updateCashDrawerReasonAction,
    getAllCashDrawerReasonsAction,
} from "../../../redux/actions/cashDrawerReasonsAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

const schema = Yup.object().shape({
    reason: Yup.string().required("Reason description is required"),
    type: Yup.string()
        .oneOf(["ADD", "REMOVE"], "Invalid reason type")
        .required("Type is required"),
});

type CashDrawerReasonFormData = Yup.InferType<typeof schema>;

export interface ICreateCashDrawerReasonDrawerProps {
    open: boolean;
    toggleDrawer: () => void;
    populateFor?: { id?: number; reason: string; type: string };
}

export const AddCashDrawerReasonDrawer: FC<ICreateCashDrawerReasonDrawerProps> = ({
    open,
    toggleDrawer,
    populateFor,
}) => {
    const dispatch = useAppDispatch();
    const {
        addCashDrawerReasonLoading,
        updateCashDrawerReasonLoading,
        deleteCashDrawerReasonLoading,
        cashDrawerReasonsLoading,
    } = useAppSelector((state) => state.cashDrawerReason);

    const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

    const { control, handleSubmit, reset, formState: { isDirty } } = useForm<CashDrawerReasonFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            reason: "",
            type: "ADD",
        },
    });
    const snackBar = useSnackbarContext();

    useEffect(() => {
        if (open) {
            reset({
                reason: populateFor?.reason || "",
                type: (populateFor?.type as "ADD" | "REMOVE") || "ADD",
            });
        }
    }, [open, populateFor, reset]);

    const handleSaveReason = async (data: CashDrawerReasonFormData) => {
        const payload = {
            storeId: 1,
            merchantId: 1,
            reason: data.reason,
            type: data.type,
        };

        try {
            if (populateFor?.id) {
                await dispatch(
                    updateCashDrawerReasonAction({ ...payload, id: populateFor.id })
                ).unwrap();
                snackBar.showSnackbar("Cash Drawer Reason updated successfully!", "success");
            } else {
                await dispatch(createCashDrawerReasonAction(payload)).unwrap();
                snackBar.showSnackbar("Cash Drawer Reason added successfully!", "success");
            }
            dispatch(getAllCashDrawerReasonsAction());
            toggleDrawer();
        } catch (error) {
            snackBar.showSnackbar(
                "Failed to save reason. Please try again.",
                "error"
            );
        }
    };

    const handleCloseAttempt = () => {
        if (isDirty) {
            setExitConfirmOpen(true);
        } else {
            toggleDrawer();
        }
    };

    return (
        <>
        <DiscardConfirmationModal
            open={exitConfirmOpen}
            onKeepEditing={() => setExitConfirmOpen(false)}
            onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
        />
        <CustomDrawer
            isOpen={open}
            toggleDrawer={handleCloseAttempt}
            sx={{ width: "28.65rem" }}
        >
            <form onSubmit={handleSubmit(handleSaveReason)}>
                <Box
                    sx={{
                        paddingInline: "1.857rem",
                        paddingTop: "2rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2.5rem",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "bold",
                            fontSize: "1.4rem",
                            lineHeight: "1.7rem",
                            color: Colors.TEXT_GRAY_DARK,
                        }}
                    >
                        {populateFor ? "Update Cash Drawer Reason" : "Add a New Cash Drawer Reason"}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                        <Typography>Reason Description</Typography>
                        <Controller
                            name="reason"
                            control={control}
                            render={({ field, fieldState }) => (
                                <CustomTextBox
                                    {...field}
                                    error={fieldState.invalid}
                                    helperText={fieldState.error?.message}
                                    sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                                    placeholder="Enter reason details"
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                        <Typography>Type</Typography>
                        <Controller
                            name="type"
                            control={control}
                            render={({ field, fieldState }) => (
                                <Box>
                                    <select
                                        {...field}
                                        style={{
                                            width: "100%",
                                            padding: "0.8rem",
                                            borderRadius: "0.4rem",
                                            border: `1px solid ${fieldState.invalid ? "red" : "#ccc"}`,
                                            backgroundColor: Colors.WHITE,
                                            fontSize: "1rem"
                                        }}
                                    >
                                        <option value="ADD">Add Cash</option>
                                        <option value="REMOVE">Remove Cash</option>
                                    </select>
                                    {fieldState.invalid && (
                                        <Typography color="error" variant="caption">
                                            {fieldState.error?.message}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                    </Box>

                    <CustomButton
                        sx={{ width: "12rem", height: "2.6rem", marginTop: "1rem" }}
                        type="submit"
                        disabled={
                            addCashDrawerReasonLoading ||
                            updateCashDrawerReasonLoading ||
                            deleteCashDrawerReasonLoading ||
                            cashDrawerReasonsLoading
                        }
                    >
                        {addCashDrawerReasonLoading ||
                            updateCashDrawerReasonLoading ||
                            deleteCashDrawerReasonLoading ||
                            cashDrawerReasonsLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : populateFor ? (
                            "Update Reason"
                        ) : (
                            "Add Reason"
                        )}
                    </CustomButton>
                </Box>
            </form>
        </CustomDrawer>
        </>
    );
};
